import { RISK_LEVEL_COLORS } from "@app/theme/tokens";
import { Box, Stack, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import type { RiskBucket } from "../types";

/** Donut of the SPOF risk split, with the total in its centre unless a label replaces it. */
export default function RiskDonut({
  buckets,
  centerLabel,
  size = 200,
}: {
  buckets: RiskBucket[];
  centerLabel?: string;
  size?: number;
}) {
  const total = buckets.reduce((sum, bucket) => sum + bucket.count, 0);
  const radius = size / 2;

  return (
    <Box
      sx={{ position: "relative", height: size, width: size, flexShrink: 0 }}
    >
      <PieChart
        width={size}
        height={size}
        hideLegend
        series={[
          {
            innerRadius: radius * 0.58,
            outerRadius: radius * 0.88,
            paddingAngle: 2,
            cornerRadius: 3,
            data: buckets.map((bucket) => ({
              id: bucket.level,
              value: bucket.count,
              label: bucket.level,
              color: RISK_LEVEL_COLORS[bucket.level],
            })),
          },
        ]}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        {centerLabel ? (
          <Typography variant="body1">{centerLabel}</Typography>
        ) : (
          <>
            <Typography
              sx={{ fontSize: "1.75rem", fontWeight: 700, lineHeight: 1.1 }}
            >
              {total}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              SPOFs
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
}

export function RiskLegend({
  buckets,
  compact = false,
}: {
  buckets: RiskBucket[];
  compact?: boolean;
}) {
  return (
    <Stack
      direction={compact ? "row" : "column"}
      spacing={compact ? 2 : 0.75}
      sx={{ flexGrow: 1, minWidth: 0, flexWrap: compact ? "wrap" : "nowrap" }}
    >
      {buckets.map((bucket) => (
        <Box
          key={bucket.level}
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: RISK_LEVEL_COLORS[bucket.level],
            }}
          />
          <Typography variant="body2" sx={{ flexGrow: compact ? 0 : 1 }}>
            {bucket.level}
          </Typography>
          {!compact && (
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
            >
              {bucket.count} ({bucket.percentage}%)
            </Typography>
          )}
        </Box>
      ))}
    </Stack>
  );
}
